/* React */
import { useEffect, useState } from 'react'

/* Material */
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import useStyles from '../styles'
import styles from './AttributeChoices.module.css'

/* Types */
import { attribute } from 'src/shared/frontendTypes/product'
import { Status } from '../AddToCart'
import type { DisabledAttributeOptions } from '../../../functions/attributeAvailability'

export type selectedAttributes = Record<string, number | ''>

interface AttributesProps {
  attributes: attribute[]
  selectedAttributes: selectedAttributes
  handleChangeAttribute: (attribut: number, option: number | '') => void
  Status: Status
  notices?: Record<number, string>
  disabledOptions?: DisabledAttributeOptions
  choices?: boolean
  idPrefix?: string
}

const selectMenuProps = {
  disableScrollLock: true,
  PaperProps: {
    sx: {
      maxHeight: 'min(360px, calc(100vh - 96px))',
      mt: .5,
      overflowY: 'auto',
      overscrollBehaviorY: 'contain',
      WebkitOverflowScrolling: 'touch',
    },
  },
  MenuListProps: {
    sx: {
      py: .5,
    },
  },
} as const

const AttributesSelector: React.FC<AttributesProps> = ({
  attributes, selectedAttributes, handleChangeAttribute, Status, disabledOptions = {}, notices = {}, idPrefix = '', choices = false
}) => {
  const classes = useStyles()
  const [openAttributeId, setOpenAttributeId] = useState<number | null>(null)

  useEffect(() => {
    if (openAttributeId === null || typeof window === 'undefined') return

    const { body, documentElement } = document
    const scrollY = window.scrollY
    const previousBodyStyles = {
      left: body.style.left,
      overflow: body.style.overflow,
      position: body.style.position,
      right: body.style.right,
      top: body.style.top,
      width: body.style.width,
    }
    const previousHtmlOverscroll = documentElement.style.overscrollBehavior

    body.style.left = '0'
    body.style.overflow = 'hidden'
    body.style.position = 'fixed'
    body.style.right = '0'
    body.style.top = `-${scrollY}px`
    body.style.width = '100%'
    documentElement.style.overscrollBehavior = 'none'

    return () => {
      body.style.left = previousBodyStyles.left
      body.style.overflow = previousBodyStyles.overflow
      body.style.position = previousBodyStyles.position
      body.style.right = previousBodyStyles.right
      body.style.top = previousBodyStyles.top
      body.style.width = previousBodyStyles.width
      documentElement.style.overscrollBehavior = previousHtmlOverscroll
      window.scrollTo(0, scrollY)
    }
  }, [openAttributeId])

  return <div>
    {attributes.map(attribute => choices && attribute.options.length <= 6 ? <fieldset className={styles.fieldset} key={attribute.id}>
      <legend>{attribute.name} <span>{typeof selectedAttributes[attribute.id] === 'number' ? 'ausgewählt' : 'auswählen'}</span></legend>
      <div className={styles.options}>
        {attribute.options.map(option => <button type='button' key={option.id}
          aria-pressed={selectedAttributes[attribute.id] === option.id}
          disabled={disabledOptions[attribute.id]?.[option.id] === true}
          onClick={() => handleChangeAttribute(attribute.id, option.id)}>
          {option.name}{disabledOptions[attribute.id]?.[option.id] && <small>Nicht bestellbar</small>}
        </button>)}
      </div>
      {typeof selectedAttributes[attribute.id] === 'number' && <button type='button'
        className={styles.clear} aria-label={`${attribute.name}-Auswahl aufheben`}
        onClick={() => handleChangeAttribute(attribute.id, '')}>Auswahl aufheben</button>}
      {(notices[attribute.id] || (Status === 'tried' && typeof selectedAttributes[attribute.id] !== 'number')) &&
        <p role='status' className={styles.notice}>{notices[attribute.id] || `Bitte ${attribute.name} auswählen.`}</p>}
    </fieldset> :
      <div key={attribute.id}>

        <TextField
          id={`${idPrefix}attribute-${attribute.id}`}
          className={`${classes.formControl} ${choices && typeof selectedAttributes[attribute.id] === 'number' ? styles.selectedField : ''}`}
          select variant={choices ? 'outlined' : 'standard'}
          label={attribute.name}
          value={selectedAttributes[attribute.id] ?? ''}
          InputLabelProps={{ shrink: true }}
          helperText={notices[attribute.id]}
          FormHelperTextProps={{ role: 'status', 'aria-live': 'polite' }}

          SelectProps={{
            MenuProps: selectMenuProps,
            displayEmpty: true,
            renderValue: value => attribute.options.find(option => option.id === value)?.name || 'Bitte wählen',
            onClose: () => setOpenAttributeId(current =>
              current === attribute.id ? null : current
            ),
            onOpen: () => setOpenAttributeId(attribute.id),
          }}

          //helperText="Wählen Sie bitte die Eigenschaft"

          onChange={({target:{value}}) => {
            const parsedValue = Number(value)
            if (choices && disabledOptions[attribute.id]?.[parsedValue]) return
            handleChangeAttribute(
              attribute.id,
              Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : ''
            )
          }}

          error={
            Status==='tried' && selectedAttributes[attribute.id] === ''
          }
        >

          <MenuItem key='placeholder' value='' disabled={!choices}>
            {choices && typeof selectedAttributes[attribute.id] === 'number' ? 'Auswahl aufheben' : 'Bitte wählen'}
          </MenuItem>

          {attribute.options?.map(option=>
            <MenuItem
              key={option.id}
              value={option.id}
              disabled={disabledOptions[attribute.id]?.[option.id] === true}
              sx={choices ? {
                '&.Mui-selected': { color: '#195731', backgroundColor: '#edf7ef', fontWeight: 600 },
                '&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': { backgroundColor: '#e0efe3' },
                '&.Mui-disabled': { opacity: .45 },
              } : undefined}
            >
              {option.name}{disabledOptions[attribute.id]?.[option.id] ? ' – nicht bestellbar' : ''}
            </MenuItem>
          )}

        </TextField>

      </div>

    )}

  </div>
  
}

export default AttributesSelector
